import { Images } from '../assets'
import React, { useState, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { Swiper, SwiperSlide } from 'swiper/react'
import { EffectFade, Autoplay } from 'swiper/modules'
import PageTitle from '../components/PageTitle'
import ImpactStat from '../components/ImpactStat'
import ManagementCard from '../components/ManagementCard'

import 'swiper/css'
import 'swiper/css/effect-fade'



const whoWeAreImages = [
  Images.AboutTeam01,
  Images.AboutTeam02,
  Images.AboutTeam03,
  Images.AboutTeam06,
  Images.AboutTeam07,
  Images.AboutTeam08,
  Images.AboutTeam09,
]

const getRandomUniqueSet = (pool, count, exclude = []) => {
  const available = pool.filter((img) => !exclude.includes(img))
  const result = []
  const used = new Set()
  while (result.length < count && used.size < available.length) {
    const candidate = available[Math.floor(Math.random() * available.length)]
    if (!used.has(candidate)) {
      used.add(candidate)
      result.push(candidate)
    }
  }
  return result
}

const pickNewImage = (current, others) => {
  const available = whoWeAreImages.filter((img) => img !== current && !others.includes(img))
  if (available.length === 0) return current
  return available[Math.floor(Math.random() * available.length)]
}

const SectionHeading = ({ children, className = '' }) => (
  <h1 className={`text-5xl font-bold text-[#2A2A2A] tracking-tight ${className}`}>{children}</h1>
)

const services = [
  {
    title: 'Digital Health Systems',
    desc: 'We design and build the platforms that run health programmes - patient registries, laboratory tracking systems, civil registration platforms, and mobile data collection tools.',
    icon: Images.TechnologyIcon,
    color: '#60A5FA',
  },
  {
    title: 'Health Data and Analytics',
    desc: 'We deploy data collection platforms at scale, configure real-time analytics dashboards, and generate the evidence that programme managers and donors need to make decisions.',
    icon: Images.DataIcon,
    color: '#34D399',
  },
  {
    title: 'Implementation and Advisory',
    desc: 'We assess existing systems, modernize legacy platforms, train teams, and provide ongoing technical support - staying engaged long after go-live.',
    icon: Images.AssessmentIcon,
    color: '#A78BFA',
  },
]

const managementTeam = [
  {
    image: Images.Adetola,
    name: 'Adetola Olateju',
    role: 'Managing Partner',
    bio: 'Adetola leads SeamHealth Innovations, setting the strategic direction of the firm and overseeing its engagements with global health clients across Nigeria and Sub-Saharan Africa. Under his leadership, SeamHealth has delivered digital health systems for UNICEF, the Bill and Melinda Gates Foundation, the Clinton Health Access Initiative, and multiple federal and state ministries of health. He has represented SeamHealth at international platforms including the INTEREST 2025 conference in Windhoek, Namibia, where the firm presented peer-reviewed research on digital feedback systems in HIV care settings.',
    linkedin: 'https://www.linkedin.com/in/adetolaolateju/',
  },
  {
    image: Images.Tosin,
    name: 'Tosin Ajayi',
    role: 'Project Manager',
    bio: 'Tosin manages the delivery of SeamHealth\'s client engagements, coordinating across technology, field implementation, and programme stakeholders to ensure projects are delivered on time and to specification. He has overseen deployments across multiple Nigerian states, including the Clientshot health insurance assessment programmes in the FCT and Nasarawa State, which collectively collected over 120,000 beneficiary responses across 224 health facilities.',
    linkedin: 'https://www.linkedin.com/in/ajayi-oluwatosin-a49245199/',
  },
  {
    image: Images.Doose,
    name: 'Doose Ayodeji',
    role: 'Admin and Finance',
    bio: 'Doose oversees the administrative and financial operations of SeamHealth Innovations, ensuring the organisational infrastructure that underpins our client engagements runs effectively. She manages the financial reporting, procurement, and operational processes that support a growing portfolio of digital health projects across Nigeria.',
    linkedin: 'https://www.linkedin.com/in/doose-ayodeji-276657200/',
  },
  {
    image: Images.Lilian,
    name: 'Lilian Ekpere',
    role: 'Senior Product Manager',
    bio: 'Lilian leads product development at SeamHealth, translating the needs of health programme clients into well-defined digital solutions. She works across the product lifecycle - from requirements gathering and solution design through to testing and post-deployment review - ensuring that SeamHealth\'s platforms are built to meet the real operational demands of health settings across Nigeria.',
    linkedin: 'https://www.linkedin.com/in/lilianekpere/',
  },
]

const About = () => {
  const initialSet = getRandomUniqueSet(whoWeAreImages, 4)
  const [img0, setImg0] = useState(initialSet[0])
  const [img1, setImg1] = useState(initialSet[1])
  const [img2, setImg2] = useState(initialSet[2])
  const [img3, setImg3] = useState(initialSet[3])

  const img0Ref = useRef(img0)
  const img1Ref = useRef(img1)
  const img2Ref = useRef(img2)
  const img3Ref = useRef(img3)

  useEffect(() => { img0Ref.current = img0 }, [img0])
  useEffect(() => { img1Ref.current = img1 }, [img1])
  useEffect(() => { img2Ref.current = img2 }, [img2])
  useEffect(() => { img3Ref.current = img3 }, [img3])

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  useEffect(() => {
    const interval = setInterval(() => {
      setImg0((prev) => pickNewImage(prev, [img1Ref.current, img2Ref.current, img3Ref.current]))
    }, 5000)
    return () => clearInterval(interval)
  }, [])

  useEffect(() => {
    const interval = setInterval(() => {
      setImg1((prev) => pickNewImage(prev, [img0Ref.current, img2Ref.current, img3Ref.current]))
    }, 7000)
    return () => clearInterval(interval)
  }, [])

  useEffect(() => {
    const interval = setInterval(() => {
      setImg2((prev) => pickNewImage(prev, [img0Ref.current, img1Ref.current, img3Ref.current]))
    }, 9000)
    return () => clearInterval(interval)
  }, [])

  useEffect(() => {
    const interval = setInterval(() => {
      setImg3((prev) => pickNewImage(prev, [img0Ref.current, img1Ref.current, img2Ref.current]))
    }, 11000)
    return () => clearInterval(interval)
  }, [])

  return (
    <>
      <PageTitle title="About | SeamHealth Innovations" />

      {/* Section 1 - Page Header */}
      <section className="project-section relative flex flex-col overflow-hidden !h-auto min-h-[70vh]">
        <video
          className="absolute inset-0 w-full h-full object-cover -z-[1000]"
          autoPlay
          loop
          muted
          playsInline
          controls={false}
          disablePictureInPicture
        >
          <source src={Images.ProjectVid} type="video/mp4" />
        </video>
        <div className="relative z-10 w-full max-w-[1280px] mx-auto px-5 sm:px-8 md:px-12 text-center pt-28 md:pt-52 pb-12 md:pb-24">
          <div data-aos="fade-up" className="max-w-[760px] mx-auto">
            <h1 className="text-3xl md:text-5xl font-bold text-white">About SeamHealth Innovations</h1>
            <p className="text-base md:text-xl text-white/70 mt-4 md:mt-6 leading-relaxed">
              A health technology consultancy building digital systems that strengthen health programmes across Africa.
            </p>
          </div>
          <div
            data-aos="fade-up"
            data-aos-delay={100}
            className="group relative mt-10 md:mt-20 rounded-2xl overflow-hidden cursor-pointer"
          >
            <Swiper
              modules={[EffectFade, Autoplay]}
              effect="fade"
              fadeEffect={{ crossFade: true }}
              slidesPerView={1}
              loop={true}
              speed={700}
              autoplay={{ delay: 5000, disableOnInteraction: false }}
              className="w-full aspect-[4/3] md:aspect-[16/9] [&_.swiper-slide-active_img]:blur-0 [&_.swiper-slide:not(.swiper-slide-active)_img]:blur-xl"
            >
              {[Images.AboutTeam05, Images.AboutTeam04].map((img, i) => (
                <SwiperSlide key={i}>
                  <img
                    src={img}
                    alt={i === 0 ? 'SeamHealth team' : 'SeamHealth team group'}
                    className="about-hero-slide-image w-full h-full object-cover scale-105 group-hover:scale-100"
                    draggable="false"
                    loading="lazy"
                  />
                </SwiperSlide>
              ))}
            </Swiper>
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A3D6E]/80 via-[#0D5BA8]/30 to-transparent transition-transform duration-300 ease-out translate-y-0 group-hover:translate-y-full pointer-events-none" />
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 z-[1] h-32 md:h-44">
          <svg viewBox="0 0 1440 200" fill="none" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" className="w-full h-full block">
            <path d="M0 200H1440V0L0 200Z" fill="white" />
          </svg>
        </div>
      </section>

      {/* Section 2 - Who We Are */}
      <section className="py-40 md:py-48 bg-white relative overflow-hidden">
        <div className="absolute top-0 left-0 w-[500px] h-[500px] -translate-y-1/4 -translate-x-1/4 rounded-full bg-[#007DD6]/5 blur-3xl pointer-events-none" />
        <div className="max-w-[1440px] mx-auto px-8 md:px-16">
          <div className="max-w-[900px] mx-auto">
            <div data-aos="fade-up">
              <SectionHeading className="text-center">Who We Are</SectionHeading>
              <div className="mt-10 space-y-8 text-[#5B5B5B] text-lg leading-[1.8]">
                <p>
                  SeamHealth Innovations is a Nigerian health technology consultancy founded to bridge the gap between the ambitions of global health programmes and the digital infrastructure needed to deliver them. We design, build, and deploy digital health systems for governments, international organisations, and implementing partners working across Sub-Saharan Africa.
                </p>
                <p>
                  Our work sits at the intersection of software engineering, public health, and field implementation. We do not hand over a system and walk away. We stay engaged through deployment, staff training, and ongoing support - because a platform that works in a test environment but fails in a rural health facility is not a solution.
                </p>
                <p>
                  We have worked with some of the most respected institutions in global health, including UNICEF, the Bill and Melinda Gates Foundation, the Clinton Health Access Initiative, the Institute of Human Virology Nigeria, SmileTrain Inc., the Global Fund, and federal and state ministries of health across Nigeria. Our deployments span HIV care, civil registration, cancer detection, laboratory services, and patient feedback systems - reaching over 143,000 beneficiaries across 249 health facilities.
                </p>
              </div>
            </div>
          </div>

          <div data-aos="fade-up" className="mt-16 md:mt-20">
            <style>{`
              @keyframes whoWeAreFade {
                from { opacity: 0; filter: blur(8px); transform: scale(1.02); }
                to { opacity: 1; filter: blur(0); transform: scale(1); }
              }
              .animate-who-we-are-fade {
                animation: whoWeAreFade 0.7s ease-in-out forwards;
              }
            `}</style>
            <div className="grid grid-cols-1 gap-4 w-full">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[img0, img1].map((img, idx) => (
                  <div key={idx} className="group relative w-full h-[260px] md:h-[280px] rounded-xl overflow-hidden shadow-lg transition-all duration-500 hover:shadow-2xl hover:-translate-y-0.5 bg-gray-100">
                    <img
                      decoding="async"
                      src={img}
                      alt="SeamHealth team"
                      className="w-full h-full object-cover object-top transition-transform duration-500 ease-out group-hover:scale-105 animate-who-we-are-fade"
                      draggable="false"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-tr from-[#0A3D6E]/70 via-[#0D5BA8]/40 to-transparent transition-opacity duration-500 group-hover:opacity-60" />
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-[minmax(0,0.3fr)_minmax(0,0.7fr)] gap-4">
                {[img2, img3].map((img, idx) => (
                  <div key={idx + 2} className="group relative w-full h-[300px] md:h-[380px] rounded-xl overflow-hidden shadow-lg transition-all duration-500 hover:shadow-2xl hover:-translate-y-0.5 bg-gray-100">
                    <img
                      decoding="async"
                      src={img}
                      alt="SeamHealth team"
                      className="w-full h-full object-cover object-top transition-transform duration-500 ease-out group-hover:scale-105 animate-who-we-are-fade"
                      draggable="false"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-tr from-[#0A3D6E]/70 via-[#0D5BA8]/40 to-transparent transition-opacity duration-500 group-hover:opacity-60" />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 3 - What We Do */}
      <section className="py-40 md:py-48 bg-[#001a33]">
        <div className="max-w-[1280px] mx-auto px-8 md:px-12">
          <div className="text-center max-w-[640px] mx-auto mb-16">
            <SectionHeading className="text-white">What We Do</SectionHeading>
            <p className="text-white/60 mt-4 leading-relaxed">
              We work across the full arc of a digital health programme - from system design and field deployment to long-term advisory and scale-up.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 md:gap-8">
            {services.map((s, i) => (
              <div
                key={i}
                data-aos="fade-up"
                data-aos-delay={i * 100}
                className="group relative rounded-xl bg-white/5 border border-white/10 p-6 md:p-8 transition-all duration-300 hover:bg-white/10 hover:border-white/20 hover:-translate-y-1 hover:shadow-xl hover:shadow-black/20"
              >
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-5" style={{ backgroundColor: `${s.color}20` }}>
                  <img decoding="async" src={s.icon} alt="" className="w-6 h-6" style={{ opacity: 0.8 }} loading="lazy" />
                </div>
                <h3 className="text-lg font-semibold text-white">{s.title}</h3>
                <p className="text-white/50 text-sm leading-relaxed mt-2">{s.desc}</p>
              </div>
            ))}
          </div>

          <div data-aos="fade-up" className="mt-14 flex justify-center">
            <Link to="/services" className="btn btn-primary text-base w-fit">
              See our full services <img decoding="async" src={Images.ArrowUpRight} alt="" loading="lazy" />
            </Link>
          </div>
        </div>
      </section>

      {/* Section 4 - Our Reach */}
      <section className="py-14 md:py-16 bg-white">
        <div className="max-w-[1280px] mx-auto px-8 md:px-12">
          <div className="text-center mb-10">
            <SectionHeading>Our Reach</SectionHeading>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12">
            <ImpactStat number="143,000+" label="Users Engaged" index={0} />
            <ImpactStat number="249" label="Health Facilities Onboarded" index={1} />
            <ImpactStat number="9+" label="Institutional Clients" index={2} />
            <ImpactStat number="2" label="Peer-Reviewed Publications" index={3} />
          </div>

          <div className="grid md:grid-cols-2 gap-12 md:gap-16 mt-16 md:mt-20">
            <div data-aos="fade-up">
              <h4 className="text-2xl font-semibold text-[#2A2A2A] mb-4">Geographic coverage</h4>
              <p className="text-[#5B5B5B] leading-relaxed">
                Our deployments span all six geopolitical zones of Nigeria, from primary health care facilities in Lagos and Ekiti to HIV programme sites in Nasarawa, Benue, and the Federal Capital Territory. We have worked at the facility level, the state level, and the national level - and our platforms are built to operate in the connectivity and resource conditions that characterise real-world health settings in Sub-Saharan Africa.
              </p>
            </div>
            <div data-aos="fade-up" data-aos-delay={100}>
              <h4 className="text-2xl font-semibold text-[#2A2A2A] mb-4">Conference presence</h4>
              <p className="text-[#5B5B5B] leading-relaxed">
                In May 2025, our team presented two peer-reviewed research posters at INTEREST 2025 in Windhoek, Namibia - one of the leading global scientific conferences on HIV treatment, pathogenesis, and prevention research. Our presentations drew on implementation evidence from our Clientshot deployments across Nigerian HIV care settings, contributing to the global evidence base on digital feedback systems and continuous quality improvement.
              </p>
            </div>
          </div>

          <div data-aos="fade-up" className="mt-14 text-center">
            <Link to="/publications" className="btn btn-primary text-base w-fit">
              Read our publications <img decoding="async" src={Images.ArrowUpRight} alt="" loading="lazy" />
            </Link>
          </div>
        </div>
      </section>

      {/* Section 5 - Management Team */}
      <section className="py-40 md:py-48 bg-[#F8F9FB]">
        <div className="max-w-[1280px] mx-auto px-8 md:px-12">
          <div className="max-w-[640px] mb-16">
            <SectionHeading>Management Team</SectionHeading>
            <p className="text-lg text-[#5B5B5B] leading-relaxed mt-4">
              The people guiding SeamHealth's strategy, delivery, and operations.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 md:gap-8">
            {managementTeam.map((member, i) => (
              <ManagementCard key={i} {...member} />
            ))}
          </div>
        </div>
      </section>
    </>
  )
}

export default About

/*
PREVIOUSLY REMOVED SECTIONS (retained as comments per request):

- Timeline: "A Decade of Building Health Systems That Work"
- Our Story dark section
- Mission section
- Vision section
- Values section
- Team carousel ("The faces behind our success")

Original imports and assets for these sections have been removed from the active file.
*/
